import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  CompactTablePagination,
  EmptyState,
  ErrorState,
  LoadingState,
  TablePageShell,
} from '@/shared/ui';

import { useStockAlerts } from '../../hooks';
import { formatQuantity } from '../../lib/format';
import { useLocationLookup } from '../../lib/useLocationLookup';
import { STOCK_ALERT_TYPE_LABELS, stockAlertTypeColor } from '../../model/options';
import { ProductIdCell } from './ProductIdCell';

export interface AlertsPanelProps {
  onOpenPolicy: (productId: string, locationId: string) => void;
}

/**
 * Tab 3 — current low-stock/out-of-stock breaches. A live-computed snapshot, not a persisted
 * alert history, so an empty result is a genuinely GOOD state (nothing needs attention) rather
 * than an absence-of-data error look.
 */
export function AlertsPanel({ onOpenPolicy }: AlertsPanelProps) {
  const { t } = useTranslation('inventory');
  const { getLocationName } = useLocationLookup();

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const query = useStockAlerts({ page, size });
  const items = query.data?.items ?? [];
  const total = query.data?.page?.totalElements ?? 0;

  if (query.isLoading) return <LoadingState label={t('alertsPanel.loading')} />;
  if (query.isError) {
    return (
      <ErrorState
        title={t('alertsPanel.loadError.title')}
        message={query.error?.message ?? t('common:error.unknown')}
        onRetry={() => void query.refetch()}
      />
    );
  }
  if (items.length === 0) {
    return (
      <EmptyState
        title={t('alertsPanel.empty.title')}
        description={t('alertsPanel.empty.description')}
        icon={<TaskAltRoundedIcon />}
      />
    );
  }

  return (
    <TablePageShell
      footer={
        <CompactTablePagination
          count={total}
          page={page}
          rowsPerPage={size}
          rowsPerPageOptions={[10, 20, 50]}
          onPageChange={(_e, next) => setPage(next)}
          onRowsPerPageChange={(e) => {
            setSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} ${t('common:pagination.of')} ${count}`
          }
        />
      }
    >
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t('alertsPanel.table.product')}</TableCell>
              <TableCell>{t('alertsPanel.table.location')}</TableCell>
              <TableCell>{t('alertsPanel.table.alertType')}</TableCell>
              <TableCell align="right">{t('alertsPanel.table.available')}</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                {t('alertsPanel.table.threshold')}
              </TableCell>
              <TableCell align="right">{t('alertsPanel.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((alert) => (
              <TableRow key={`${alert.productId}:${alert.locationId}`} hover>
                <TableCell>
                  <ProductIdCell productId={alert.productId} />
                </TableCell>
                <TableCell>
                  <Tooltip title={alert.locationId}>
                    <Typography variant="body2">{getLocationName(alert.locationId)}</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={stockAlertTypeColor(alert.alertType)}
                    label={t(STOCK_ALERT_TYPE_LABELS[alert.alertType])}
                  />
                </TableCell>
                <TableCell align="right">{formatQuantity(alert.availableQty)}</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  {alert.threshold != null ? formatQuantity(alert.threshold) : '—'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={t('alertsPanel.rowActions.policy')}>
                    <IconButton
                      size="small"
                      aria-label={t('alertsPanel.rowActions.policyAria', {
                        productId: alert.productId,
                      })}
                      onClick={() => onOpenPolicy(alert.productId, alert.locationId)}
                    >
                      <TuneRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </TablePageShell>
  );
}
